import { FileResponseType } from '@ts/common.types';
import { PublisherWebsiteImportType } from '@ts/publishers.types';
import { getBlobFromBase64 } from '@utils/createFileFromBytesArray';
import downloadFile from '@utils/downloadCSV';
import {
  downloadPublisherWebsiteImportReportApi,
  getPublisherWebsiteImportTemplateApi,
} from '@api/publishers.api';
import { PublisherWebsiteImportStatus } from 'respona_api/generated/order-publisher_pb';

import { ApiError } from 'src/errors/ApiError';

import {
  CSV_EXTENSION,
  CSV_MIME_TYPE,
  GRPC_CODE_UNAVAILABLE,
  GRPC_CODE_UNKNOWN,
  GRPC_WEB_NO_HEADERS_MESSAGE,
  IMPORT_REPORT_FILE_NAME,
  IMPORT_TEMPLATE_FILE_NAME,
} from './constants';

/**
 * Data rows of a CSV, the same way the backend counts them: every non-blank line but the header.
 * Quoted multi-line cells are not expected in this template, so a plain line split is enough
 * for a pre-check; the backend re-validates on launch.
 */
export const countCsvDataRows = (content: string): number => {
  const lines = content.split(/\r\n|\r|\n/).filter((line) => line.trim().length > 0);

  return Math.max(0, lines.length - 1);
};

/**
 * True for failures that happened on the wire rather than in the backend: the grpc-web
 * client never got response headers, or the service was unavailable. Worth one retry and a
 * human message instead of the raw transport text.
 */
export const isTransportError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) {
    return false;
  }

  if (error.code === GRPC_CODE_UNAVAILABLE) {
    return true;
  }

  return error.code === GRPC_CODE_UNKNOWN && error.message?.includes(GRPC_WEB_NO_HEADERS_MESSAGE);
};

/** Saves a backend `FileResponse` as a .csv; the backend title carries no extension. */
export const saveCsvFileResponse = (file: FileResponseType, fallbackName: string): void => {
  if (!file?.fileBytes) {
    throw new Error('File not found');
  }

  const name = file.title || fallbackName;
  const fileName = name.toLowerCase().endsWith(CSV_EXTENSION) ? name : `${name}${CSV_EXTENSION}`;

  downloadFile(fileName, getBlobFromBase64(file.fileBytes, CSV_MIME_TYPE));
};

export const downloadImportTemplate = (): Promise<void> =>
  getPublisherWebsiteImportTemplateApi().then((file) =>
    saveCsvFileResponse(file, IMPORT_TEMPLATE_FILE_NAME)
  );

export const downloadImportReport = (importId: number, workspaceId: number): Promise<void> =>
  downloadPublisherWebsiteImportReportApi(importId, workspaceId).then((file) =>
    saveCsvFileResponse(file, `${IMPORT_REPORT_FILE_NAME}-${importId}`)
  );

export const getProcessedRows = (item: PublisherWebsiteImportType): number =>
  Math.max(0, item.totalRows - item.pendingRows);

export const getProgressPercentage = (item: PublisherWebsiteImportType): number => {
  if (!item?.totalRows) {
    return 0;
  }

  return Math.min(100, Math.round((getProcessedRows(item) / item.totalRows) * 100));
};

export const getImportStatusLabel = (item: PublisherWebsiteImportType): string => {
  switch (item?.status) {
    case PublisherWebsiteImportStatus.PUBLISHER_WEBSITE_IMPORT_COMPLETED:
      return 'Completed';
    case PublisherWebsiteImportStatus.PUBLISHER_WEBSITE_IMPORT_CANCELED:
      return 'Canceled';
    default:
      return 'In progress';
  }
};

export const getImportSummary = (item: PublisherWebsiteImportType): string =>
  `${item.createdRows} created, ${item.skippedRows} skipped, ${item.failedRows} failed`;
